// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable import/no-unresolved */
import { useEffect, useState, useRef } from 'react';
import EventSource from 'eventsource'; // Use the polyfill for broader browser support
import { useSelector } from 'react-redux';
import env from '../Utils/Constants/env';

const baseURL = `${env.baseUri}`;
const useSSE = (endpoint, eventListener) => {
	// Get the current protocol (http or https)
	const currentProtocol = window.location.protocol;

	// Get the current domain
	const currentDomain = window.location.host;

	const { auth } = useSelector((state) => state.auth);
	const eventSourceURL =
		process.env.NODE_ENV === 'development'
			? `${baseURL}${endpoint}`
			: `${currentProtocol}//${currentDomain}/api/v1/web/${endpoint}`;
	
	const [sseData, setSSEData] = useState(null);
	const [connectionState, setConnectionState] = useState('connecting');
	const [retryCount, setRetryCount] = useState(0);
	const retryCountRef = useRef(0);

	useEffect(() => {
		if (!auth.token) {
			setConnectionState('no-auth');
			return undefined;
		}

		let eventSourceInstance;
		let reconnectTimer;
		let connectionTimeout;
		const maxRetries = 5;
		const timeoutDuration = 30000; // 30 seconds

		const createEventSourceWithToken = (urlSource, token) => {
			const headers = {
				Authorization: `Bearer ${token}`,
				Accept: '*/*',
				connection: 'keep-alive',
				AcceptEncoding: 'gzip, deflate, br',
			};

			return new EventSource(urlSource, { headers });
		};

		const connectEventSource = () => {
			setConnectionState('connecting');
			
			eventSourceInstance = createEventSourceWithToken(eventSourceURL, auth.token);

			// Set connection timeout
			connectionTimeout = setTimeout(() => {
				if (
					eventSourceInstance &&
					eventSourceInstance.readyState === EventSource.CONNECTING
				) {
					eventSourceInstance.close();
					setConnectionState('timeout');
					attemptReconnect();
				}
			}, timeoutDuration);

			// Connection opened successfully
			eventSourceInstance.onopen = () => {
				clearTimeout(connectionTimeout);
				setConnectionState('connected');
				retryCountRef.current = 0; // Reset retry count on successful connection
				setRetryCount(0);
			};

			// Handle incoming data
			eventSourceInstance.addEventListener(eventListener, (event) => {
				try {
					const data = JSON.parse(event.data);
					const uniqueDrivers = data?.filter(
						(driver, index, self) =>
							index === self.findIndex((t) => t.user.id === driver.user.id),
					);
					setSSEData(uniqueDrivers);
					setConnectionState('connected');
				} catch (error) {
					setConnectionState('parse-error');
				}
			});

			// Handle errors
			eventSourceInstance.onerror = () => {
				clearTimeout(connectionTimeout);
				setConnectionState('error');
				if (eventSourceInstance) {
					eventSourceInstance.close();
				}
				attemptReconnect();
			};
		};

		const attemptReconnect = () => {
			if (retryCountRef.current < maxRetries) {
				const retryDelay = Math.min(1000 * 2 ** retryCountRef.current, 30000); // Exponential backoff, max 30s
				retryCountRef.current += 1;
				setRetryCount(retryCountRef.current);

				reconnectTimer = setTimeout(() => {
					connectEventSource();
				}, retryDelay);
			} else {
				setConnectionState('max-retries-reached');
			}
		};

		connectEventSource();

		// Cleanup function
		return () => {
			if (eventSourceInstance) {
				eventSourceInstance.close();
			}
			if (reconnectTimer) {
				clearTimeout(reconnectTimer);
			}
			if (connectionTimeout) {
				clearTimeout(connectionTimeout);
			}
			setConnectionState('closed');
		};
	}, [auth.token, eventSourceURL, eventListener]);

	return { data: sseData, connectionState, retryCount };
};

export default useSSE;
